import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, Home } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
const FIELDS = [['first_name', 'First name'], ['last_name', 'Last name'], ['email', 'Email'], ['phone_mobile', 'Phone'],
  ['address_num', 'House no.'], ['address_street', 'Street'], ['address_suburb', 'Suburb'], ['address_city', 'Town / city']];

export default function ReconnectPublicPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [done, setDone] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/public/reconnect/${token}`).then(r => {
      setD(r.data);
      setForm({ ...r.data, services: [], book_inspection: false, best_time: '', preferred_contact: 'Phone', notes: '' });
    }).catch(e => setError(e?.response?.data?.detail || 'This link is not valid.'));
  }, [token]);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/public/reconnect/${token}`, form); setDone('thanks'); }
    catch (err) { setError(err?.response?.data?.detail || 'Could not send — please try again.'); }
    finally { setBusy(false); }
  };
  const unsubscribe = async () => {
    setBusy(true);
    try { await api.post(`/public/reconnect/${token}/unsubscribe`); setDone('unsub'); } catch { setError('Could not unsubscribe — please reply to the email instead.'); }
    finally { setBusy(false); }
  };
  const shell = (children) => (
    <div className="min-h-screen bg-[#F7F6F3] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-2 mb-6 text-[#D35400]"><Home size={20} /><span className="font-bold tracking-tight text-slate-900">Roofs To Remedy</span></div>
        {children}
      </div>
    </div>
  );
  if (error && !d) return shell(<p data-testid="reconnect-public-error" className="text-slate-700">{error}</p>);
  if (!d) return shell(<Loader2 className="animate-spin text-slate-400" />);
  if (done) return shell(
    <div data-testid="reconnect-public-done" className="bg-white rounded-lg border border-[#E2E2DF] p-8 text-center">
      <CheckCircle2 className="mx-auto text-emerald-600 mb-3" size={36} />
      <h1 className="text-xl font-bold text-slate-900">{done === 'unsub' ? "You're unsubscribed" : 'Thank you!'}</h1>
      <p className="text-sm text-slate-600 mt-2">{done === 'unsub' ? "We won't send you any more of these letters." : form.book_inspection ? "We'll be in touch soon to arrange your free roof inspection." : "Your details are updated."}</p>
    </div>);
  if (params.get('unsubscribe') === '1') return shell(
    <div className="bg-white rounded-lg border border-[#E2E2DF] p-8">
      <h1 className="text-xl font-bold text-slate-900">Unsubscribe</h1>
      <p className="text-sm text-slate-600 mt-2 mb-5">Stop receiving letters and offers from Roofs To Remedy?</p>
      <button data-testid="reconnect-unsubscribe-confirm-btn" disabled={busy} onClick={unsubscribe} className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold">Yes, unsubscribe me</button>
    </div>);
  return shell(
    <form onSubmit={submit} data-testid="reconnect-public-form" className="bg-white rounded-lg border border-[#E2E2DF] p-6 sm:p-8 space-y-6">
      <div>
        {d.unsubscribed && <p data-testid="reconnect-already-unsubscribed" className="mb-3 text-xs px-3 py-2 rounded bg-slate-100 text-slate-600">You're unsubscribed from our letters. You can still update your details or book an inspection below.</p>}
        <h1 className="text-2xl font-bold text-slate-900">Hi {d.first_name || 'there'}, are your details still right?</h1>
        <p className="text-sm text-slate-600 mt-2">As a past client you get a <b>free roof inspection</b>
          {Number(d.maintenance_discount_pct) > 0 && <>, <b>{d.maintenance_discount_pct}% off maintenance</b></>}
          {Number(d.other_discount_pct) > 0 && <> and <b>{d.other_discount_pct}% off other roofing work</b></>}.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {FIELDS.map(([k, label]) => (
          <label key={k} className={`flex flex-col gap-1 ${['email', 'address_street'].includes(k) ? 'col-span-2' : ''}`}>
            <span className="text-xs font-semibold text-slate-600">{label}</span>
            <input data-testid={`reconnect-field-${k}`} value={form[k] || ''} onChange={e => set(k, e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D35400]/30" />
          </label>
        ))}
      </div>
      <label className="flex items-start gap-3 p-4 rounded-md bg-orange-50 border border-orange-200 cursor-pointer">
        <input data-testid="reconnect-book-inspection" type="checkbox" checked={form.book_inspection} onChange={e => set('book_inspection', e.target.checked)} className="mt-1" />
        <span><span className="font-semibold text-slate-900">Yes, book my free roof inspection</span><br /><span className="text-xs text-slate-600">We'll call or email to arrange a time.</span></span>
      </label>
      <div>
        <p className="text-xs font-semibold text-slate-600 mb-2">Anything you're interested in?</p>
        <div className="flex flex-wrap gap-2">
          {d.services.map(s => (
            <button type="button" key={s} data-testid={`reconnect-service-${s.replace(/\s+/g, '-').toLowerCase()}`} onClick={() => set('services', form.services.includes(s) ? form.services.filter(x => x !== s) : [...form.services, s])}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${form.services.includes(s) ? 'bg-[#D35400] text-white border-[#D35400]' : 'border-slate-300 text-slate-700 hover:bg-slate-50'}`}>{s}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1"><span className="text-xs font-semibold text-slate-600">Best way to contact you</span>
          <select data-testid="reconnect-preferred-contact" value={form.preferred_contact} onChange={e => set('preferred_contact', e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md"><option>Phone</option><option>Email</option><option>Text</option></select></label>
        <label className="flex flex-col gap-1"><span className="text-xs font-semibold text-slate-600">Best time</span>
          <input data-testid="reconnect-best-time" value={form.best_time} onChange={e => set('best_time', e.target.value)} placeholder="e.g. weekday mornings" className="px-3 py-2 text-sm border border-slate-300 rounded-md" /></label>
      </div>
      <label className="flex flex-col gap-1"><span className="text-xs font-semibold text-slate-600">Anything else?</span>
        <textarea data-testid="reconnect-notes" rows={3} value={form.notes} onChange={e => set('notes', e.target.value)} className="px-3 py-2 text-sm border border-slate-300 rounded-md" /></label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button data-testid="reconnect-submit-btn" disabled={busy} className="w-full py-3 rounded-md bg-[#D35400] hover:bg-[#A04000] text-white font-semibold transition-colors disabled:opacity-50">{busy ? 'Sending…' : 'Send'}</button>
      <div className="flex justify-between text-xs text-slate-500">
        {d.website_url ? <a data-testid="reconnect-website-link" href={d.website_url} target="_blank" rel="noreferrer" className="hover:underline">Prefer our website? Book there</a> : <span />}
        <a data-testid="reconnect-unsubscribe-link" href={`?unsubscribe=1`} className="hover:underline">Unsubscribe</a>
      </div>
    </form>);
}
